import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';

export type OfflinePaymentMethodType =
  | 'venmo'
  | 'cashapp'
  | 'paypal'
  | 'zelle'
  | 'check'
  | 'cash'
  | 'custom';

export interface OrgPaymentMethodConfig {
  type: OfflinePaymentMethodType;
  enabled: boolean;
  label: string;
  identifier?: string;
  preferred?: boolean;
}

export interface OrgPaymentConfig {
  methods: OrgPaymentMethodConfig[];
  includeStripeInRequests?: boolean;
  customMessage?: string;
}

interface PaymentConfigResponse {
  success: boolean;
  data: OrgPaymentConfig | null;
}

interface PaymentConfigSaveResponse {
  success: boolean;
  data: OrgPaymentConfig;
}

export function usePaymentConfig(orgId: string) {
  const [config, setConfig] = useState<OrgPaymentConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<PaymentConfigResponse>(`/payment-methods/${orgId}`);
      setConfig(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load payment config');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const save = useCallback(
    async (data: OrgPaymentConfig) => {
      const res = await api.put<PaymentConfigSaveResponse>(`/payment-methods/${orgId}`, data);
      setConfig(res.data);
      return res.data;
    },
    [orgId],
  );

  return { config, loading, error, save, refetch: fetchConfig };
}

// ── Payment Schedule Defaults ──────────────────────────────────────────────

export interface PaymentScheduleDefaults {
  collectDeposit: boolean;
  depositType: 'fixed' | 'percent';
  depositAmount?: number;
  balanceCollection?: 'before_move' | 'on_completion' | 'split';
  splitBeforeMovePercent?: number;
}

export function usePaymentScheduleDefaults(orgId: string) {
  const [defaults, setDefaults] = useState<PaymentScheduleDefaults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDefaults = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ success: boolean; data: PaymentScheduleDefaults | null }>(
        `/payment-methods/${orgId}/schedule-defaults`,
      );
      setDefaults(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load schedule defaults');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchDefaults();
  }, [fetchDefaults]);

  const save = useCallback(
    async (data: PaymentScheduleDefaults) => {
      const res = await api.put<{ success: boolean; data: PaymentScheduleDefaults }>(
        `/payment-methods/${orgId}/schedule-defaults`,
        data,
      );
      setDefaults(res.data);
      return res.data;
    },
    [orgId],
  );

  return { defaults, loading, error, save, refetch: fetchDefaults };
}
